import React, { useMemo, useState } from 'react';
import { BellIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CampaignFormModal from './push-notifications/CampaignFormModal';
import SendConfirmModal from './push-notifications/SendConfirmModal';
import type { PushNotificationCampaign, TargetAudience } from '../../models/MasterAdmin/pushNotificationCampaign';
import { getDefaultScreenTypeForAudience } from '../../models/MasterAdmin/notificationScreenTypes';

type VendorPushAudience = 'SINGLE_DOCTOR' | 'SINGLE_AMBULANCE';

type Props = {
  vendorId: string;
  vendorName?: string;
  targetAudience: VendorPushAudience;
  className?: string;
  compact?: boolean;
};

const VendorPushNotificationLauncher: React.FC<Props> = ({
  vendorId,
  vendorName,
  targetAudience,
  className = '',
  compact = false,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<PushNotificationCampaign | null>(null);
  const [sendTargetCampaign, setSendTargetCampaign] = useState<PushNotificationCampaign | null>(null);

  const initialCampaign = useMemo<PushNotificationCampaign>(
    () => ({
      id: '',
      title: '',
      message: '',
      imageUrl: null,
      notificationType: 'REMINDER',
      intentType: getDefaultScreenTypeForAudience(targetAudience as TargetAudience),
      intentValue: '',
      targetAudience,
      targetPlatform: 'BOTH',
      filterCity: null,
      filterState: null,
      targetUserId: vendorId,
      status: 'DRAFT',
      scheduledAt: null,
      scheduleTimezone: 'Asia/Kolkata',
      sentAt: null,
      lastSendMeta: null,
      createdById: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
    [targetAudience, vendorId]
  );

  const openComposer = () => {
    setEditingCampaign(initialCampaign);
    setFormOpen(true);
  };

  const label = vendorName?.trim()
    ? `Send push to ${vendorName.trim()}`
    : targetAudience === 'SINGLE_DOCTOR'
      ? 'Send push to doctor'
      : 'Send push to ambulance vendor';

  return (
    <>
      <button
        type="button"
        onClick={openComposer}
        title={label}
        className={
          className ||
          (compact
            ? 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 active:scale-95 transition-all shadow-sm'
            : 'inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 active:scale-95 transition-all shadow-sm border border-violet-500/20')
        }
      >
        {compact ? (
          <PaperAirplaneIcon className="h-4 w-4" />
        ) : (
          <BellIcon className="h-4 w-4" />
        )}
        {compact ? 'Push' : 'Send Push Notification'}
      </button>

      <CampaignFormModal
        open={formOpen}
        initial={editingCampaign}
        onClose={() => {
          setFormOpen(false);
          setEditingCampaign(null);
        }}
        onSaved={() => {
          toast.success('Notification draft saved');
        }}
        onReadyToSend={(campaign) => {
          setSendTargetCampaign(campaign);
          setSendConfirmOpen(true);
        }}
      />

      {sendConfirmOpen && sendTargetCampaign && (
        <SendConfirmModal
          open
          campaign={sendTargetCampaign}
          onClose={() => {
            setSendConfirmOpen(false);
            setSendTargetCampaign(null);
            setFormOpen(false);
            setEditingCampaign(null);
          }}
          onSent={() => {
            toast.success('Push notification sent to vendor');
          }}
        />
      )}
    </>
  );
};

export default VendorPushNotificationLauncher;
